import React from "react";
import { X, Check, Loader2, Undo2 } from "lucide-react";
import { proxied } from "@/lib/imageProxy";
import { AI_BG_PRESETS, AI_BG_PRESET_KEY } from "@/components/AiBgPresetSelector";

function presetLabel(key) {
  const k = key || AI_BG_PRESET_KEY;
  return AI_BG_PRESETS.find((p) => p.key === k)?.label || "Beton stúdió";
}

/**
 * Side-by-side összehasonlító modal AI háttércsere után.
 * originals + processed képek pároban jelennek meg + "Elfogadom" / opcionális "Visszaállítás".
 */
export function AiBgComparison({ open, originals = [], processed = [], preset, onAccept, onRevert, retrying }) {
  if (!open) return null;

  const pairs = processed.map((p, i) => ({ before: originals[i] || p, after: p }));
  const presetName = presetLabel(preset);

  return (
    <div
      data-testid="aibg-comparison-modal"
      className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4 overflow-hidden"
    >
      <div className="bg-anthracite-900 border border-white/10 rounded-3xl w-full max-w-4xl max-h-[min(90dvh,100%)] min-w-0 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div>
            <div className="font-heading text-lg font-medium text-white">AI háttércsere — előnézet</div>
            <div className="text-xs text-slate-400">
              Háttér: <b className="text-electric">{presetName}</b> · {processed.length} kép feldolgozva
            </div>
          </div>
          <button
            type="button"
            data-testid="aibg-comparison-close"
            onClick={onAccept}
            className="w-9 h-9 rounded-full bg-white/[0.05] hover:bg-white/[0.1] flex items-center justify-center text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div className="space-y-4">
            {pairs.map((p, i) => (
              <div key={i} data-testid={`aibg-comparison-pair-${i}`} className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-black/70 text-[10px] tracking-widest uppercase text-slate-300">
                    Eredeti
                  </div>
                  <div className="aspect-video rounded-xl overflow-hidden bg-anthracite-800 border border-white/5">
                    <img src={proxied(p.before)} alt="eredeti" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-electric/90 text-[10px] tracking-widest uppercase text-white">
                    AI ({presetName})
                  </div>
                  <div className="aspect-video rounded-xl overflow-hidden bg-anthracite-800 border border-electric/30">
                    <img src={proxied(p.after)} alt="ai" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="action-bar-responsive p-4 sm:p-5 border-t border-white/5 shrink-0">
          {onRevert && (
            <button
              type="button"
              data-testid="aibg-comparison-revert"
              onClick={onRevert}
              disabled={retrying}
              className="action-bar-leading inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 text-red-300 text-sm disabled:opacity-50 whitespace-normal text-center"
            >
              <Undo2 className="w-4 h-4 shrink-0" /> Visszaállítás eredetire
            </button>
          )}
          <button
            type="button"
            data-testid="aibg-comparison-accept"
            onClick={onAccept}
            disabled={retrying}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium disabled:opacity-50"
          >
            <Check className="w-4 h-4 shrink-0" /> Elfogadom
          </button>
        </div>
      </div>
    </div>
  );
}
